import { ProviderCategory } from '@/api/model';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { ImageUrlsField } from '@/components/ui/image-urls-field';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import React from 'react';
import type { UseFormReturn } from 'react-hook-form';

export interface VoucherProviderFormData {
  name: string;
  email: string;
  address: string;
  phone?: string;
  short_description?: string;
  logo_urls?: string[]; // Image upload field - contains uploaded image URLs
  category: ProviderCategory;
}

interface VoucherProviderFormProps {
  form: UseFormReturn<VoucherProviderFormData>;
  onSubmit: (data: VoucherProviderFormData) => void;
  isSubmitting?: boolean;
  submitText?: string;
  onCancel?: () => void;
  cancelText?: string;
  hideLogoField?: boolean;
}

export const VoucherProviderForm: React.FC<VoucherProviderFormProps> = ({
  form,
  onSubmit,
  isSubmitting = false,
  submitText = 'Speichern',
  onCancel,
  cancelText = 'Abbrechen',
  hideLogoField = false,
}) => {
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
        <Card as='section'>
          <CardHeader>
            <CardTitle>Grundinformationen</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <FormField
                control={form.control}
                name='name'
                rules={{
                  required: 'Name ist erforderlich',
                  maxLength: {
                    value: 255,
                    message: 'Name muss kürzer als 255 Zeichen sein',
                  },
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='text-sm font-bold tracking-wide text-neutral-dunkelgrau'>
                      Name des Partners
                      <span className='text-swd-tuerkis'>*</span>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder='Partnernamen eingeben' className='h-9 rounded-xl' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='category'
                rules={{ required: 'Kategorie ist erforderlich' }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='text-sm font-bold tracking-wide text-neutral-dunkelgrau'>
                      Kategorie<span className='text-swd-tuerkis'>*</span>
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className='h-9 rounded-xl'>
                          <SelectValue placeholder='Kategorie auswählen' />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={ProviderCategory.GASTRONOMIE}>Gastronomie</SelectItem>
                        <SelectItem value={ProviderCategory.ERLEBEN}>Erleben</SelectItem>
                        <SelectItem value={ProviderCategory.SHOPPING}>Shopping</SelectItem>
                        <SelectItem value={ProviderCategory.SERVICES}>Services</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name='short_description'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Kurzbeschreibung</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder='Kurze Beschreibung des Partners'
                      className='min-h-40 rounded-xl'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Kontaktdaten</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <FormField
                control={form.control}
                name='email'
                rules={{
                  required: 'E-Mail ist erforderlich',
                  maxLength: {
                    value: 255,
                    message: 'E-Mail muss kürzer als 255 Zeichen sein',
                  },
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Ungültige E-Mail-Adresse',
                  },
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='text-sm font-bold tracking-wide text-neutral-dunkelgrau'>
                      E-Mail-Adresse<span className='text-swd-tuerkis'>*</span>
                    </FormLabel>
                    <FormControl>
                      <Input type='email' placeholder='E-Mail-Adresse eingeben' className='h-9 rounded-xl' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='phone'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Telefonnummer</FormLabel>
                    <FormControl>
                      <Input placeholder='Telefonnummer eingeben' className='h-9 rounded-xl' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name='address'
              rules={{ required: 'Adresse ist erforderlich' }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className='text-sm font-bold tracking-wide text-neutral-dunkelgrau'>
                    Adresse<span className='text-swd-tuerkis'>*</span>
                  </FormLabel>
                  <FormControl>
                    <Textarea placeholder='Vollständige Adresse eingeben' className='min-h-40 rounded-xl' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {!hideLogoField && (
          <Card>
            <CardHeader>
              <CardTitle>Weitere Informationen</CardTitle>
            </CardHeader>
            <CardContent>
              <ImageUrlsField control={form.control} name='logo_urls' label='Logo' maxImages={1} />
            </CardContent>
          </Card>
        )}

        <div className='flex gap-6 items-center justify-end'>
          {onCancel && (
            <Button type='button' variant='outline' size='sm' onClick={onCancel}>
              {cancelText}
            </Button>
          )}
          <Button
            type='submit'
            size='sm'
            disabled={isSubmitting}
            className='bg-swd-petrol-grau text-white hover:bg-swd-petrol-grau/90'
          >
            {isSubmitting ? 'Wird verarbeitet...' : submitText}
          </Button>
        </div>
      </form>
    </Form>
  );
};
